import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { Job, User } from "../types";
import { jobRowToJob } from "../types";
import type { JobRow } from "../types";

/**
 * Pour partenaire / poseur / commercial : liste des jobs à afficher.
 * Connexion par code : chargement via get-tenant-jobs-by-code (contourne RLS si auth.uid ≠ profiles.id).
 */
export function usePartnerInstallerJobs(
  currentUser: User | null,
  tenantJobs: Job[],
  refetchTenantJobs: () => void
): { jobs: Job[]; loading: boolean; refetch: () => Promise<void> } {
  const [jobsByCode, setJobsByCode] = useState<Job[]>([]);
  const [loadingByCode, setLoadingByCode] = useState(false);

  const isExternalActor =
    currentUser?.role === "partner" ||
    currentUser?.role === "installer" ||
    currentUser?.role === "commercial";

  const useCodeFallback = isExternalActor && Boolean(currentUser?.code?.trim());

  const fetchByCode = useCallback(async () => {
    if (!currentUser?.code || !isExternalActor) return;
    setLoadingByCode(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-tenant-jobs-by-code", {
        body: { code: currentUser.code, role: currentUser.role },
      });
      const raw = (data as { jobs?: JobRow[]; error?: string } | null) ?? {};
      if (raw.error) {
        console.warn("[usePartnerInstallerJobs] get-tenant-jobs-by-code:", raw.error);
        setJobsByCode([]);
        return;
      }
      const rows = (raw.jobs ?? []) as JobRow[];
      setJobsByCode(rows.map((r) => jobRowToJob(r)));
    } catch (e) {
      console.warn("[usePartnerInstallerJobs]", e);
      setJobsByCode([]);
    } finally {
      setLoadingByCode(false);
    }
  }, [currentUser?.code, currentUser?.role, isExternalActor]);

  useEffect(() => {
    if (!useCodeFallback) return;
    fetchByCode();
  }, [useCodeFallback, fetchByCode]);

  const refetch = useCallback(async () => {
    if (useCodeFallback) {
      await fetchByCode();
      return;
    }
    refetchTenantJobs();
  }, [useCodeFallback, refetchTenantJobs, fetchByCode]);

  if (!isExternalActor || !currentUser) {
    return { jobs: [], loading: false, refetch: async () => {} };
  }

  if (useCodeFallback) {
    return {
      jobs: jobsByCode,
      loading: loadingByCode,
      refetch,
    };
  }

  const filtered =
    currentUser.role === "partner"
      ? tenantJobs.filter((j) => String(j.partnerId ?? "") === String(currentUser.id))
      : currentUser.role === "commercial"
        ? tenantJobs.filter((j) => String(j.commercialId ?? "") === String(currentUser.id))
        : tenantJobs.filter((j) => String(j.installerId ?? "") === String(currentUser.id));

  return { jobs: filtered, loading: false, refetch: async () => refetchTenantJobs() };
}
